import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AdminLoginLayout } from '@/layouts/AdminLoginLayout';
import { BuyerLayout } from '@/layouts/BuyerLayout';
// LEGACY: HomePage Spec §M2 mise de côté 2026-05-10 — voir ADR-018.
// Restaurable en décommentant cet import + la ligne `{ index: true, element: <HomePage /> }`
// plus bas, et en remplaçant `<DealsLegacyRedirect />` par `<DealsPage />` sur `path: 'deals'`.
// import { HomePage } from '@/routes/public/HomePage';
import { AboutPage } from '@/routes/public/AboutPage';
import { FaqPage } from '@/routes/public/FaqPage';
import { ContactPage } from '@/routes/public/ContactPage';
import { LegalPage } from '@/routes/public/LegalPage';
import { DealsPage } from '@/routes/public/DealsPage';
import { DealPage } from '@/routes/public/DealPage';
import { SellPage } from '@/routes/public/SellPage';
import { SignInPage } from '@/routes/auth/SignInPage';
import { SignUpPage } from '@/routes/auth/SignUpPage';
import { ResetPasswordPage } from '@/routes/auth/ResetPasswordPage';
import { ResetPasswordConfirmPage } from '@/routes/auth/ResetPasswordConfirmPage';
import { VerifyPage } from '@/routes/auth/VerifyPage';
import { VerifySuccessPage } from '@/routes/auth/VerifySuccessPage';
import { AuthCallbackPage } from '@/routes/auth/AuthCallbackPage';
import { DashboardPage } from '@/routes/dashboard/DashboardPage';
import { DashboardProfilePage } from '@/routes/dashboard/DashboardProfilePage';
import { DashboardSavedPage } from '@/routes/dashboard/DashboardSavedPage';
import { DashboardRequestsPage } from '@/routes/dashboard/DashboardRequestsPage';
import { DashboardHistoryPage } from '@/routes/dashboard/DashboardHistoryPage';
import { AdminPage } from '@/routes/admin/AdminPage';
import { AdminDealsPage } from '@/routes/admin/AdminDealsPage';
import { AdminDealEditPage } from '@/routes/admin/AdminDealEditPage';
import { AdminDealNewPage } from '@/routes/admin/AdminDealNewPage';
import { AdminImportPage } from '@/routes/admin/AdminImportPage';
import { AdminSubmissionsPage } from '@/routes/admin/AdminSubmissionsPage';
import { AdminRequestsPage } from '@/routes/admin/AdminRequestsPage';
import { AdminUsersPage } from '@/routes/admin/AdminUsersPage';
import { AdminIntegrationsPage } from '@/routes/admin/AdminIntegrationsPage';
// AdminEmailsPage is lazy-loaded because it pulls TipTap + DOMPurify (~130 KB
// gzip) into its chunk — admins rarely visit it, so we keep them out of the
// main admin bundle. Default export from AdminEmailsPage.tsx.
const AdminEmailsPage = lazy(() => import('@/routes/admin/AdminEmailsPage'));
const AdminCmsPage = lazy(() => import('@/routes/admin/AdminCmsPage'));
const AdminAdvisorsPage = lazy(() => import('@/routes/admin/AdminAdvisorsPage'));
const AdminSettingsPage = lazy(() => import('@/routes/admin/AdminSettingsPage'));
import { AdminLoginPage } from '@/routes/admin/AdminLoginPage';
import { Page403 } from '@/routes/system/Page403';
import { Page404 } from '@/routes/system/Page404';
import { Page500 } from '@/routes/system/Page500';
import { RequireRole } from '@/lib/route-guards';
import { AppRoot } from './AppRoot';

// Spec V1.1 uses `/deals/{slug}/` (plural). User prompt mentioned
// `/deal/:slug` — kept the spec convention here. Rerouting from any other
// pattern will be handled in Phase 4 when the catalogue ships.

// ADR-018: legacy `/deals` route bounces to `/` (catalogue) while preserving
// search params and hash so external links like /deals?zone=marina&page=2 or
// /deals#catalogue-grid keep working transparently. Since the SEO lot 1,
// netlify.toml 301s `/deals` at the edge first — this client redirect is the
// safety net for in-app navigations and non-Netlify environments.
function DealsLegacyRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/', search, hash }} replace />;
}

const router = createBrowserRouter([
  {
    element: <AppRoot />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          // LEGACY: HomePage Spec §M2 mise de côté 2026-05-10 — voir ADR-018.
          // { index: true, element: <HomePage /> },
          { index: true, element: <DealsPage /> }, // ADR-018 : `/` sert le catalogue
          { path: 'about', element: <AboutPage /> },
          { path: 'faq', element: <FaqPage /> },
          { path: 'contact', element: <ContactPage /> },
          { path: 'legal/privacy', element: <LegalPage slug="privacy" /> },
          { path: 'legal/terms', element: <LegalPage slug="terms" /> },
          { path: 'legal/cookies', element: <LegalPage slug="cookies" /> },
          { path: 'deals', element: <DealsLegacyRedirect /> }, // ADR-018 : redirect /deals → /
          { path: 'deals/:slug', element: <DealPage /> },
          { path: 'sell', element: <SellPage /> },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <RequireRole role="buyer">
            <BuyerLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'profile', element: <DashboardProfilePage /> },
          { path: 'saved', element: <DashboardSavedPage /> },
          { path: 'requests', element: <DashboardRequestsPage /> },
          { path: 'history', element: <DashboardHistoryPage /> },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: 'signin', element: <SignInPage /> },
          { path: 'signup', element: <SignUpPage /> },
          { path: 'verify', element: <VerifyPage /> },
          { path: 'verify/success', element: <VerifySuccessPage /> },
          { path: 'auth/callback', element: <AuthCallbackPage /> },
          { path: 'reset-password', element: <ResetPasswordPage /> },
          { path: 'reset-password/confirm', element: <ResetPasswordConfirmPage /> },
        ],
      },
      {
        // Spec V1.1 §5.7 — fond ink plein, pas de header/footer public.
        element: <AdminLoginLayout />,
        children: [{ path: 'admin/login', element: <AdminLoginPage /> }],
      },
      {
        path: 'admin',
        element: (
          <RequireRole role={['admin', 'super_admin']}>
            <AdminLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: <AdminPage /> },
          { path: 'deals', element: <AdminDealsPage /> },
          { path: 'deals/new', element: <AdminDealNewPage /> },
          { path: 'deals/:id/edit', element: <AdminDealEditPage /> },
          { path: 'import', element: <AdminImportPage /> },
          { path: 'submissions', element: <AdminSubmissionsPage /> },
          { path: 'requests', element: <AdminRequestsPage /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'integrations', element: <AdminIntegrationsPage /> },
          {
            path: 'emails',
            element: (
              <Suspense fallback={<div aria-busy="true" />}>
                <AdminEmailsPage />
              </Suspense>
            ),
          },
          {
            path: 'cms',
            element: (
              <Suspense fallback={<div aria-busy="true" />}>
                <AdminCmsPage />
              </Suspense>
            ),
          },
          { path: 'legal', element: <Navigate to="/admin/cms" replace /> },
          {
            path: 'advisors',
            element: (
              <Suspense fallback={<div aria-busy="true" />}>
                <AdminAdvisorsPage />
              </Suspense>
            ),
          },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<div aria-busy="true" />}>
                <AdminSettingsPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <PublicLayout />,
        children: [
          { path: 'system/403', element: <Page403 /> },
          { path: 'system/500', element: <Page500 /> },
          { path: '*', element: <Page404 /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
